import {
    useEffect, useRef,
} from 'react';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from './use-session';
import { useAgencySwitcher } from './use-agency-switcher';
import { AuthQueryFactory } from '../utils/query-factory';

function useRouteBoundary({
    agencyId,
    onFailure,
}: {
    agencyId: string | null;
    onFailure: () => void;
}) {
    const queryClient = useQueryClient();
    const currentAgencyId = useRef<number | null>(null);

    // We'll call useSession() to grab details about the session
    const {
        switchUserPortfolio,
        isSwitchingPortfolio,
    } = useAgencySwitcher();

    const { isError, isLoading, data } = useSession();

    useEffect(() => {
        if (!isError) return;

        onFailure();
    }, [isError, onFailure]);

    // Switch the user's portfolio when the agencyId changes
    useEffect(() => {
        if (agencyId === null) return;
        if (isLoading) return;
        if (data === undefined) return;

        const parsedAgencyId = z.object({
            value: z.coerce.number().positive().int().finite()
                .gt(0),
        }).safeParse({
            value: agencyId,
        });

        if (!parsedAgencyId.success) return;

        if (data.complianceAgencyId === parsedAgencyId.data.value) return;

        switchUserPortfolio(parsedAgencyId.data.value);
    }, [agencyId, isLoading, data, switchUserPortfolio]);

    // Invalidate queries when detecting an agency change
    useEffect(() => {
        if (data?.complianceAgencyId === undefined) return;
        if (currentAgencyId.current === data.complianceAgencyId) return;
        if (currentAgencyId.current === null) {
            currentAgencyId.current = data.complianceAgencyId;
            return;
        }

        currentAgencyId.current = data.complianceAgencyId;

        void queryClient.invalidateQueries({
            queryKey: [],
            predicate: (query) => query.queryKey[0] !== 'auth',
        });

        void queryClient.invalidateQueries({
            queryKey: AuthQueryFactory.systemStates(),
        });

        void queryClient.invalidateQueries({
            queryKey: AuthQueryFactory.userAgencies(),
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.complianceAgencyId]);

    return [
        isSwitchingPortfolio || isLoading || isError,
        data?.requiresRelogin ?? false,
    ];
}

export { useRouteBoundary };
